import { logEvent } from '@/helpers/eventLogger';
import { errorReporter } from '@/services/errorReporter';
import { safeLocalStorage } from '@/services/safeStorage';
import axios from 'axios';
import { useEffect, useMemo } from 'react';
import { v4 } from 'uuid';

const GUEST_ID_KEY = 'guest_id';

// Where storage is denied the id can't outlive the page, but it still has to be one id
// for the whole visit: the backend joins files and user-event rows on it, so minting a
// fresh one per mount would scatter a single session across several identities.
let sessionGuestId: string | undefined;

const readOrAssignGuestId = (): string => {
  const stored = safeLocalStorage.getItem(GUEST_ID_KEY);
  if (stored) return stored;
  if (sessionGuestId) return sessionGuestId;

  sessionGuestId = v4();
  // Deliberately logged even when the write is refused — the id is real and about to be
  // sent on every request; only persistence is missing.
  safeLocalStorage.setItem(GUEST_ID_KEY, sessionGuestId);
  logEvent({ logType: 'EVENT', eventType: 'GUEST_ID_ASSIGNED', trace: sessionGuestId });

  return sessionGuestId;
};

export function useGuestId() {
  const GUEST_ID = useMemo(readOrAssignGuestId, []);

  useEffect(() => {
    axios.defaults.headers.common = {
      'x-guest-id': GUEST_ID,
    };
    // The same id the backend stores on files and user-event rows, so a Sentry issue
    // can be traced to the customer's actual documents.
    errorReporter.identifyGuest(GUEST_ID);
  }, [GUEST_ID]);
}
